// src/services/intelligenceProfileService.ts
import { IndustryOnboardingData, IndustryConfig } from '../types'
import { generateId } from '../lib/utils'

export interface CompleteIntelligenceProfile {
  timestamp: string
  profileId: string
  industry: {
    id: string
    name: string
    icon: string
  }
  company: {
    name: string
    contactPerson: string
    email: string
    phone: string | null
  }
  businessProfile: {
    type: string
    typeName: string | undefined
    annualRevenue: string
    companySize: string
    location: {
      city: string
      state: string
      zipCode: string
      serviceRadius: string
    }
  }
  revenueIntelligence: {
    streams: Array<{
      type: string
      typeName: string | undefined
      percentage: number
      partners: string[]
      notes: string
    }>
    totalPercentage: number
  }
  supplyChainIntelligence: {
    suppliers: Array<{
      category: string
      categoryName: string | undefined
      primarySupplier: string
      monthlySpend: string
      alternativeSuppliers: string[]
    }>
  }
  competitiveIntelligence: {
    serviceArea: {
      city: string
      state: string
      zipCode: string
      radius: string
    }
    knownCompetitors: string[]
    competitiveAdvantages: string[]
  }
  serviceSelection: {
    id: string
    name: string
    price: number
    tierLevel: string
    billingType: string // 'one-time' or 'monthly' - from services.config.ts
    deliveryFormats: string[]
  }
  dataIntegration?: {
    integrationType?: string
    integrationStatus?: string
  }
  deliveryPreferences: {
    frequency: string
    formats: string[]
    alertThreshold: string
    priorities: string[]
    deliveryEmail?: string
  }
  paymentInfo: {
    paymentIntentId?: string
    paymentStatus: 'pending' | 'succeeded' | 'failed' | 'not_required'
    amount: number
    currency: string
    processedAt?: string
    paymentMethod?: {
      last4: string
      brand: string
    }
    billingDetails?: {
      cardholderName: string
      email: string
      companyName?: string
    }
  }
  additionalNotes: string
  metadata: {
    completedScreens: number
    totalScreens: number
    completionPercentage: number
    timeCompleted: string
    deviceInfo: {
      userAgent: string
      language: string
      timezone: string
    }
  }
}

export interface ProfileSummary {
  industry: string
  companyName: string
  businessType: string | undefined
  revenueStreams: number
  suppliers: number
  competitors: number
  selectedService: string
  serviceTierLevel: string
  paymentStatus: string
  deliveryFrequency: string
  contactEmail: string
  profileId: string
}

export class IntelligenceProfileService {
  private static readonly STORAGE_KEY = 'completed-intelligence-profile'
  private static readonly FAILED_SUBMISSIONS_KEY = 'failed-submissions'
  private static readonly API_ENDPOINT = '/api/intelligence-profiles'

  static createCompleteProfile(
    data: IndustryOnboardingData, 
    industryConfig: IndustryConfig
  ): CompleteIntelligenceProfile {
    return {
      timestamp: new Date().toISOString(),
      profileId: generateId(),
      
      industry: {
        id: data.selectedIndustry,
        name: industryConfig.name,
        icon: industryConfig.icon
      },
      
      company: {
        name: data.contactInfo.companyName,
        contactPerson: data.contactInfo.contactPerson,
        email: data.contactInfo.email,
        phone: data.contactInfo.phone || null
      },
      
      businessProfile: {
        type: data.businessProfile.businessType,
        typeName: industryConfig.businessTypes.find(t => t.id === data.businessProfile.businessType)?.name,
        annualRevenue: data.businessProfile.annualRevenue,
        companySize: data.businessProfile.companySize,
        location: data.businessProfile.location
      },
      
      revenueIntelligence: {
        streams: data.revenueStreams.map(stream => ({
          type: stream.type,
          typeName: industryConfig.revenueStreamTypes.find(t => t.id === stream.type)?.name,
          percentage: stream.percentage,
          partners: stream.partners || [],
          notes: stream.notes || ''
        })),
        totalPercentage: data.revenueStreams.reduce((sum, stream) => sum + stream.percentage, 0)
      },
      
      supplyChainIntelligence: {
        suppliers: data.suppliers.map(supplier => ({
          category: supplier.category,
          categoryName: industryConfig.supplierCategories.find(c => c.id === supplier.category)?.name,
          primarySupplier: supplier.primarySupplier,
          monthlySpend: supplier.monthlySpend,
          alternativeSuppliers: supplier.alternativeSuppliers || []
        }))
      },
      
      competitiveIntelligence: {
        serviceArea: data.competitiveProfile.serviceArea,
        knownCompetitors: data.competitiveProfile.knownCompetitors,
        competitiveAdvantages: data.competitiveProfile.competitiveAdvantages
      },
      
      serviceSelection: data.selectedService ? {
        id: data.selectedService.id,
        name: data.selectedService.name,
        price: data.selectedService.price,
        tierLevel: data.selectedService.tierLevel || 'standard',
        billingType: data.selectedService.billingType || 'one-time', // Include billing type from service selection
        deliveryFormats: data.selectedService.deliveryFormats || []
      } : {
        id: 'market-pulse-executive',
        name: 'Market Pulse Executive',
        price: 0,
        tierLevel: 'quick',
        billingType: 'one-time', // Default billing type for free service
        deliveryFormats: ['email-pdf']
      },

      dataIntegration: undefined,
      
      deliveryPreferences: {
        frequency: data.deliveryPreferences.frequency,
        formats: data.deliveryPreferences.format,
        alertThreshold: data.deliveryPreferences.alertThreshold,
        priorities: data.deliveryPreferences.priorities,
        deliveryEmail: data.deliveryPreferences.deliveryEmail || data.contactInfo.email
      },

      paymentInfo: data.paymentInfo ? {
        paymentIntentId: data.paymentInfo.paymentIntentId,
        paymentStatus: data.paymentInfo.paymentStatus || 'pending',
        amount: data.paymentInfo.amount || data.selectedService?.price || 0,
        currency: data.paymentInfo.currency || 'usd',
        processedAt: data.paymentInfo.processedAt,
        paymentMethod: data.paymentInfo.paymentMethod ? {
          last4: data.paymentInfo.paymentMethod.last4 || '****',
          brand: data.paymentInfo.paymentMethod.brand || 'unknown'
        } : undefined,
        billingDetails: data.paymentInfo.billingDetails ? {
          cardholderName: data.paymentInfo.billingDetails.cardholderName,
          email: data.paymentInfo.billingDetails.email,
          companyName: data.paymentInfo.billingDetails.companyName
        } : undefined
      } : {
        paymentStatus: data.selectedService?.price === 0 ? 'not_required' : 'pending',
        amount: data.selectedService?.price || 0,
        currency: 'usd'
      },
      
      additionalNotes: data.additionalNotes || '',
      
      metadata: {
        completedScreens: 9,
        totalScreens: 9,
        completionPercentage: 100,
        timeCompleted: new Date().toISOString(),
        deviceInfo: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      }
    }
  }

  static createSummary(
    completeProfile: CompleteIntelligenceProfile,
    industryConfig: IndustryConfig
  ): ProfileSummary {
    return {
      industry: industryConfig.name,
      companyName: completeProfile.company.name,
      businessType: completeProfile.businessProfile.typeName,
      revenueStreams: completeProfile.revenueIntelligence.streams.length,
      suppliers: completeProfile.supplyChainIntelligence.suppliers.length,
      competitors: completeProfile.competitiveIntelligence.knownCompetitors.length,
      selectedService: completeProfile.serviceSelection.name,
      serviceTierLevel: completeProfile.serviceSelection.tierLevel,
      paymentStatus: completeProfile.paymentInfo.paymentStatus,
      deliveryFrequency: completeProfile.deliveryPreferences.frequency,
      contactEmail: completeProfile.company.email,
      profileId: completeProfile.profileId
    }
  }

  static saveToLocalStorage(profile: CompleteIntelligenceProfile): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(profile))
    } catch (error) {
      console.error('Failed to save profile to localStorage:', error)
    }
  }

  static async submitToServer(profile: CompleteIntelligenceProfile): Promise<any> {
    try {
      const response = await fetch(this.API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profile)
      })
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`)
      }
      
      const result = await response.json()
      console.log('Profile submitted successfully:', result)
      return result
      
    } catch (error) {
      console.error('Failed to submit profile:', error)
      this.saveFailedSubmission(profile, error.message)
      throw error
    }
  }

  static saveFailedSubmission(profile: CompleteIntelligenceProfile, errorMessage: string): void {
    try {
      const failedSubmissions = JSON.parse(
        localStorage.getItem(this.FAILED_SUBMISSIONS_KEY) || '[]'
      )
      
      failedSubmissions.push({
        timestamp: new Date().toISOString(),
        profileId: profile.profileId,
        data: profile,
        error: errorMessage
      })
      
      localStorage.setItem(this.FAILED_SUBMISSIONS_KEY, JSON.stringify(failedSubmissions))
    } catch (error) {
      console.error('Failed to save failed submission:', error)
    }
  }

  static generateSuccessMessage(summary: ProfileSummary): string {
    return `Setup Complete!

Your strategic intelligence profile has been configured:
- Industry: ${summary.industry}
- Company: ${summary.companyName}
- Business Type: ${summary.businessType || 'Not specified'}
- Revenue streams: ${summary.revenueStreams}
- Supplier categories: ${summary.suppliers}
- Known competitors: ${summary.competitors}
- Selected Service: ${summary.selectedService} (${summary.serviceTierLevel.toUpperCase()})
- Payment Status: ${summary.paymentStatus.replace('_', ' ').toUpperCase()}
- Delivery: ${summary.deliveryFrequency}

Profile ID: ${summary.profileId}
Contact: ${summary.contactEmail}

First report will be delivered within 4 hours.`
  }

  static async processCompletion(
    data: IndustryOnboardingData,
    industryConfig: IndustryConfig
  ): Promise<{ success: boolean; profile: CompleteIntelligenceProfile; summary: ProfileSummary }> {
    // Create complete profile
    const completeProfile = this.createCompleteProfile(data, industryConfig)
    
    // Create summary
    const summary = this.createSummary(completeProfile, industryConfig)
    
    // Save to localStorage as backup
    this.saveToLocalStorage(completeProfile)
    
    try {
      // Attempt server submission
      await this.submitToServer(completeProfile)
      return { success: true, profile: completeProfile, summary }
    } catch (error) {
      // Return with success=false but still provide the data
      return { success: false, profile: completeProfile, summary }
    }
  }
}